import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { Timestamp, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { firebaseApp, isFirebaseConfigured, getFirestoreDb } from '../firebase/config';
import {
  subscribeLeads,
  updateLeadStatus,
  updateLeadTags,
  deleteLeadById,
  leadsToCsv,
  getLeadDate,
  LEAD_STATUS,
} from '../firebase/leadsAdmin';

const STATUSES = [LEAD_STATUS.NEW, LEAD_STATUS.CONTACTED, LEAD_STATUS.CLOSED];

const AdminLeads = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [tagEditId, setTagEditId] = useState(null);
  const [tagDraft, setTagDraft] = useState('');

  const handleLogout = async () => {
    if (isFirebaseConfigured() && firebaseApp) {
      try {
        await signOut(getAuth(firebaseApp));
      } catch (e) {
        console.error(e);
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('auth_provider');
    navigate('/admin/login');
  };

  useEffect(() => {
    if (!isFirebaseConfigured()) {
      setLoadError(new Error('Firebase is not configured.'));
      return undefined;
    }
    const unsub = subscribeLeads(
      (list) => {
        setRows(list);
        setLoadError(null);
      },
      (err) => setLoadError(err)
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (to) to.setHours(23, 59, 59, 999);

    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      const d = getLeadDate(r);
      if (from && (!d || d < from)) return false;
      if (to && (!d || d > to)) return false;
      if (!q) return true;
      const blob = `${r.name || ''} ${r.phone || ''} ${r.email || ''} ${r.message || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [rows, statusFilter, dateFrom, dateTo, search]);

  const analytics = useMemo(() => {
    const base = { new: 0, contacted: 0, closed: 0 };
    for (const r of rows) {
      const s = r.status || 'new';
      if (base[s] !== undefined) base[s] += 1;
    }
    return { ...base, total: rows.length };
  }, [rows]);

  const onStatusChange = useCallback(async (id, status) => {
    try {
      await updateLeadStatus(id, status);
    } catch (e) {
      console.error(e);
      alert('Could not update status.');
    }
  }, []);

  const onDelete = useCallback(async (id, name) => {
    if (!window.confirm(`Delete lead "${name || id}"?`)) return;
    try {
      await deleteLeadById(id);
    } catch (e) {
      console.error(e);
      alert('Could not delete lead.');
    }
  }, []);

  const exportCsv = useCallback(() => {
    const csv = leadsToCsv(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  const saveTags = async (id) => {
    const tags = tagDraft
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      await updateLeadTags(id, tags);
      setTagEditId(null);
    } catch (e) {
      console.error(e);
      alert('Could not save tags.');
    }
  };

  const saveFollowUp = async (id, isoLocal) => {
    try {
      const db = getFirestoreDb();
      if (!db) return;
      await updateDoc(doc(db, 'leads', id), {
        followUpAt: isoLocal ? Timestamp.fromDate(new Date(isoLocal)) : null,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      alert('Could not save follow-up date.');
    }
  };

  if (!isFirebaseConfigured()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
          <p className="text-gray-800 font-medium mb-2">Firebase not configured</p>
          <p className="text-sm text-gray-600 mb-4">Set REACT_APP_FIREBASE_* in .env.local and rebuild.</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-4 h-auto py-3 sm:h-16 sm:py-0">
            <div className="flex items-center gap-4">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Lead CRM</h1>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="text-sm text-blue-600 hover:underline"
              >
                Dashboard
              </button>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600 hidden sm:inline">Larawans Digital</span>
              <button
                type="button"
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loadError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {loadError.message || 'Failed to load leads'}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: analytics.total, color: '#1F2937' },
            { label: 'New', value: analytics.new, color: '#3B82F6' },
            { label: 'Contacted', value: analytics.contacted, color: '#F59E0B' },
            { label: 'Closed', value: analytics.closed, color: '#10B981' },
          ].map((a) => (
            <div key={a.label} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase">{a.label}</p>
              <p className="text-2xl font-bold mt-1" style={{ color: a.color }}>
                {a.value}
              </p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6 flex flex-col lg:flex-row flex-wrap gap-4 items-stretch lg:items-end">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, phone, email…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="w-full sm:w-40">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-44">
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="w-full sm:w-44">
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={exportCsv}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm hover:bg-gray-800"
            >
              Export CSV ({filtered.length})
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-700">Date</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Name</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Phone</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Email</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Source</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Status</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Tags / Follow-up</th>
                  <th className="text-left p-3 font-semibold text-gray-700">Message</th>
                  <th className="p-3 font-semibold text-gray-700 w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-8 text-center text-gray-500">
                      No leads match filters.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => {
                    const d = getLeadDate(r);
                    const fu = r.followUpAt?.toDate?.() || null;
                    return (
                      <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50 align-top">
                        <td className="p-3 whitespace-nowrap text-gray-600">
                          {d ? d.toLocaleString() : '—'}
                        </td>
                        <td className="p-3 font-medium text-gray-900">{r.name || '—'}</td>
                        <td className="p-3 text-gray-700">{r.phone || '—'}</td>
                        <td className="p-3 text-gray-700 break-all max-w-[160px]">{r.email || '—'}</td>
                        <td className="p-3 text-gray-600">{r.source || 'website'}</td>
                        <td className="p-3">
                          <select
                            value={r.status || LEAD_STATUS.NEW}
                            onChange={(e) => onStatusChange(r.id, e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 text-xs"
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3 max-w-[220px]">
                          {tagEditId === r.id ? (
                            <div className="flex flex-col gap-1">
                              <input
                                type="text"
                                value={tagDraft}
                                onChange={(e) => setTagDraft(e.target.value)}
                                placeholder="tag1, tag2"
                                className="border rounded px-2 py-1 text-xs w-full"
                              />
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  className="text-xs bg-gray-900 text-white px-2 py-0.5 rounded"
                                  onClick={() => saveTags(r.id)}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-gray-600"
                                  onClick={() => setTagEditId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="text-left text-xs text-blue-600 hover:underline"
                              onClick={() => {
                                setTagEditId(r.id);
                                setTagDraft(
                                  Array.isArray(r.tags) ? r.tags.join(', ') : r.tags || ''
                                );
                              }}
                            >
                              {Array.isArray(r.tags) && r.tags.length
                                ? r.tags.join(', ')
                                : '+ tags'}
                            </button>
                          )}
                          <div className="mt-2">
                            <input
                              type="datetime-local"
                              defaultValue={
                                fu
                                  ? new Date(fu.getTime() - fu.getTimezoneOffset() * 60000)
                                      .toISOString()
                                      .slice(0, 16)
                                  : ''
                              }
                              onBlur={(e) => saveFollowUp(r.id, e.target.value)}
                              className="border rounded px-1 py-0.5 text-[10px] w-full max-w-[155px]"
                              title="Follow-up reminder (saved on blur)"
                            />
                          </div>
                        </td>
                        <td className="p-3 text-gray-600 max-w-xs">
                          <p className="line-clamp-4 whitespace-pre-wrap">{r.message || '—'}</p>
                        </td>
                        <td className="p-3">
                          <button
                            type="button"
                            onClick={() => onDelete(r.id, r.name)}
                            className="text-red-600 text-xs hover:underline"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLeads;
